import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "@/components/layout/Seo";
import { Reveal, Overline } from "@/components/Reveal";
import { ServiceIcon } from "@/components/ServiceIcon";
import { FinalCta } from "@/components/sections/CallToActions";
import { useLang } from "@/context/LanguageContext";
import NotFoundPage from "@/pages/NotFoundPage";
import { SERVICE_IMAGES, SERVICE_IMAGE_FIT } from "@/data/serviceImages";
import { sentenceCase } from "@/lib/text";

export default function ServiceDetailPage() {
  const { lang, t } = useLang();
  const { serviceId } = useParams();
  const service = t.services.find((s) => s.id === serviceId);

  if (!service) return <NotFoundPage />;

  return (
    <>
      <Seo
        page="services"
        title={`${sentenceCase(service.name)} | ${t.brandName}`}
        description={service.short}
      />
      <section className="py-14 md:py-20" data-testid="service-detail">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Link
              to={`/${lang}/services`}
              data-testid="service-back-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-navy/60 hover:text-ser-purple transition-colors"
            >
              <ArrowLeft size={16} strokeWidth={1.5} className="transition-transform group-hover:-translate-x-1" />
              {t.servicesPage.title}
            </Link>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <Reveal delay={0.05}>
                <div className="w-14 h-14 flex items-center justify-center bg-pale-violet mb-8">
                  <ServiceIcon name={service.icon} size={26} />
                </div>
                <Overline>{t.servicesPage.overline}</Overline>
                <h1 className="font-display text-4xl md:text-5xl leading-[1.08] tracking-tight text-navy mt-4">
                  {sentenceCase(service.name)}
                </h1>
                <p className="mt-8 text-base md:text-lg leading-relaxed text-navy/75">{service.intro}</p>
                {service.note && (
                  <blockquote className="mt-8 border-l-2 border-ser-purple pl-6">
                    <p className="font-display italic text-xl md:text-2xl leading-snug text-navy/85">{service.note}</p>
                  </blockquote>
                )}
              </Reveal>
              {SERVICE_IMAGES[service.id] && (
                <Reveal delay={0.2}>
                  <div className="relative mt-10">
                    {SERVICE_IMAGE_FIT[service.id] !== "contain" && (
                      <div className="absolute -top-4 -left-4 w-full h-full border border-ser-purple/20" aria-hidden="true" />
                    )}
                    <img
                      src={SERVICE_IMAGES[service.id]}
                      alt={service.name}
                      loading="lazy"
                      className={`relative w-full ${SERVICE_IMAGE_FIT[service.id] === "contain" ? "aspect-[4/3] object-contain bg-white border border-ser-purple/25 p-3 md:p-4" : "aspect-[16/10] object-cover"}`}
                    />
                  </div>
                </Reveal>
              )}
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="border border-navy/10 bg-white p-8 md:p-10">
                  <h2 className="text-xs uppercase tracking-[0.2em] font-medium text-ser-purple">
                    {t.servicesPage.capabilitiesTitle}
                  </h2>
                  <ul className="mt-6 space-y-3.5">
                    {service.capabilities.map((c) => (
                      <li key={c} className="flex items-start gap-3 text-sm md:text-base text-navy/80">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-ser-blue shrink-0" aria-hidden="true" />
                        {c}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/${lang}/contact`}
                    data-testid="service-detail-cta"
                    className="group mt-8 inline-flex items-center gap-2 text-sm font-medium text-ser-purple hover:text-navy transition-colors"
                  >
                    {t.cta}
                    <ArrowRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
